import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { EMPTY, forkJoin, of, switchMap } from 'rxjs';
import {
  catchError,
  filter,
  map,
  mergeMap,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { DsToastService } from '@bmw-ds/components';
import { TranslateService } from '@ngx-translate/core';
import { LiabilityActionEnum } from '../../shared/enums/liability-action.enum';
import { IDamage } from '../../damages/damages.models';
import { selectCurrentDamage } from './liabilities.selectors';
import {
  deleteFile,
  deleteFileFailure,
  deleteFileSuccess,
  getLiability,
  getLiabilityFailure,
  getLiabilitySuccess,
  initializeLiabilityPage,
  loadDamageWithFiles,
  loadDamageWithFilesFailure,
  loadDamageWithFilesSuccess,
  loadExistingDamages,
  loadExistingDamagesFailure,
  loadExistingDamagesSuccess,
  loadReturnDamages,
  loadReturnDamagesFailure,
  loadReturnDamagesSuccess,
  loadThirdPartyDamages,
  loadThirdPartyDamagesFailure,
  loadThirdPartyDamagesSuccess,
  loadTransportationStages,
  loadTransportationStagesFailure,
  loadTransportationStagesSuccess,
  updateLiabilityState,
  updateLiabilityStateFailure,
  updateLiabilityStateSuccess,
  uploadNewFile,
  uploadNewFileFailure,
  uploadNewFileSuccess,
} from './liabilities.actions';
import { LiabilitiesService } from '../liabilities.service';
import { DamagesService } from '../../damages/damages.service';
import { loadDocumentTypes } from '../../shared/upload-files/state/upload-files.actions';

@Injectable()
export class LiabilitiesEffects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);
  private readonly service = inject(LiabilitiesService);
  private readonly damagesService = inject(DamagesService);
  private readonly toastService = inject(DsToastService);
  private readonly translate = inject(TranslateService);

  private pushPositiveToast(id: string, key: string): void {
    this.toastService.pushToast({
      id,
      tone: 'positive',
      toastText: this.translate.instant(key),
    });
  }

  public initializeLiabilityPage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(initializeLiabilityPage),
      mergeMap(({ id, filterHistoryByUserComments }) => [
        getLiability({ id, filterHistoryByUserComments }),
        loadDamageWithFiles({ damageId: +id }),
        loadDocumentTypes(),
      ])
    )
  );

  public getLiability$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getLiability),
      switchMap(({ id, filterHistoryByUserComments }) =>
        this.service.getLiabilityById(id, filterHistoryByUserComments).pipe(
          switchMap((liability) => {
            return of(getLiabilitySuccess({ liability }));
          }),
          catchError((err) => of(getLiabilityFailure({ error: err })))
        )
      )
    )
  );

  public updateLiabilityState$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateLiabilityState),
      switchMap((model) =>
        this.service.updateLiabilityState(model).pipe(
          map(({ status, cycles, isProcessEnd }) =>
            updateLiabilityStateSuccess({
              action: model.action,
              liabilityId: model.liabilityId,
              comment: model.comment,
              status,
              cycles,
              isProcessEnd,
              masterDataLabel: model.masterDataLabel,
            })
          ),
          catchError((err) => of(updateLiabilityStateFailure({ error: err })))
        )
      )
    )
  );

  public damageWithFiles$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadDamageWithFiles),
      switchMap(({ damageId }) =>
        this.damagesService.getDamageById(damageId, true).pipe(
          map((damage) => loadDamageWithFilesSuccess(damage)),
          catchError((err) => of(loadDamageWithFilesFailure({ error: err })))
        )
      )
    )
  );

  public uploadNewFile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(uploadNewFile),
      switchMap(({ damageId, file, documentType }) => {
        const files = Array.isArray(file) ? file : [file];

        const uploadNewFileRequests = files.map((item) =>
          this.damagesService.uploadNewFile(damageId, item, documentType)
        );

        return forkJoin(uploadNewFileRequests).pipe(
          switchMap((responses) => {
            const uploadFileRequests = responses.map((response, index) =>
              this.uploadPresignedFile(response, files[index])
            );

            return forkJoin(uploadFileRequests).pipe(
              switchMap(() => [
                loadDamageWithFiles({ damageId }),
                uploadNewFileSuccess(),
              ])
            );
          }),
          catchError((error) => of(uploadNewFileFailure({ error })))
        );
      })
    )
  );

  private uploadPresignedFile(
    response: {
      files: { fileName: string; etag?: string; preSignedUrl: string }[];
    },
    file: File
  ) {
    const fileResult = response.files.find(
      (entry) => entry.fileName === file.name && !entry.etag
    );
    if (!fileResult) {
      return of(null);
    }
    return this.damagesService.uploadFile(file, fileResult.preSignedUrl);
  }

  public deleteFile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deleteFile),
      switchMap(({ fileIds }) => {
        if (fileIds.length === 0) {
          return EMPTY;
        }

        const deleteRequests = fileIds.map(({ damageId, fileId }) =>
          this.damagesService.deleteFile(damageId, fileId)
        );

        return forkJoin(deleteRequests).pipe(
          switchMap(() => [
            loadDamageWithFiles({ damageId: fileIds[0].damageId }),
            deleteFileSuccess(),
          ]),
          catchError((error) => of(deleteFileFailure({ error })))
        );
      })
    )
  );

  public loadReturnDamages$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadReturnDamages),
      switchMap(({ vin }) =>
        this.damagesService.getReturnDamages(vin).pipe(
          map((damages) => loadReturnDamagesSuccess({ damages })),
          catchError((error) => of(loadReturnDamagesFailure({ error })))
        )
      )
    )
  );

  public loadExistingDamages$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadExistingDamages),
      switchMap(({ vin }) =>
        this.damagesService.getExistingDamagesByVin(vin).pipe(
          map((damages) => loadExistingDamagesSuccess({ damages })),
          catchError((error) => of(loadExistingDamagesFailure({ error })))
        )
      )
    )
  );

  public loadThirdPartyDamages$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadThirdPartyDamages),
      switchMap(({ damageId }) =>
        this.damagesService.getThirdPartyDamages(damageId).pipe(
          map((damages) => loadThirdPartyDamagesSuccess({ damages })),
          catchError((error) => of(loadThirdPartyDamagesFailure({ error })))
        )
      )
    )
  );

  public loadTransportationStages$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadTransportationStages),
      switchMap(({ vin, cause }) =>
        this.service.getTransportationStages(vin, cause).pipe(
          map((stages) =>
            loadTransportationStagesSuccess({
              options: [...stages]
                .map((stage) => ({
                  id: `${stage.causer.id}|${stage.fromSurveyPoint.id}|${stage.toSurveyPoint.id}`,
                  label:
                    stage.fromSurveyPoint.name && stage.toSurveyPoint.name
                      ? `${stage.fromSurveyPoint.name} - ${stage.toSurveyPoint.name}`
                      : stage.causer.name,
                }))
                .sort((first, second) =>
                  first.label.localeCompare(second.label)
                ),
            })
          ),
          catchError((error) => of(loadTransportationStagesFailure({ error })))
        )
      )
    )
  );

  public refetchAfterStatusChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateLiabilityStateSuccess),
      withLatestFrom(this.store.select(selectCurrentDamage)),
      map(([, damage]) => damage),
      filter((damage): damage is IDamage => !!damage),
      map((damage) =>
        getLiability({
          id: String(damage.id),
          filterHistoryByUserComments: true,
        })
      )
    )
  );

  public liabilityStatusChangedToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(updateLiabilityStateSuccess),
        tap(({ action, masterDataLabel }) => {
          if (action === LiabilityActionEnum.CHANGE_CAUSER_BY_SUPERUSER) {
            this.pushPositiveToast(
              'causer-changed',
              'liabilities.toasts.causerChanged'
            );
            return;
          }
          if (action === LiabilityActionEnum.CHANGE_MASTER_DATA_BY_SUPERUSER) {
            this.toastService.pushToast({
              id: 'master-data-changed',
              tone: 'positive',
              toastText: this.translate.instant(
                'liabilities.toasts.masterDataChanged',
                { masterData: masterDataLabel ?? '' }
              ),
            });
            return;
          }
          this.pushPositiveToast(
            'liability-status-changed',
            'liabilities.toasts.liabilityStatusChanged'
          );
        })
      ),
    { dispatch: false }
  );

  public deleteFileToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(deleteFileSuccess),
        tap(() =>
          this.pushPositiveToast(
            'delete-file-success',
            'liabilities.toasts.deletionSuccessful'
          )
        )
      ),
    { dispatch: false }
  );

  public uploadFileToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(uploadNewFileSuccess),
        tap(() =>
          this.pushPositiveToast(
            'created-file-success',
            'liabilities.toasts.uploadSuccessful'
          )
        )
      ),
    { dispatch: false }
  );
}
